import { useTradingStore } from '../hooks/useTradingStore'
import { Activity, TrendingUp, TrendingDown, BarChart2 } from 'lucide-react'
import clsx from 'clsx'

export default function IndicatorPanel() {
  const { indicators, currentBar } = useTradingStore()

  const emaFast = indicators.ema_fast
  const emaSlow = indicators.ema_slow
  const macd = indicators.macd

  // Determine trend
  const emaTrend = emaFast && emaSlow
    ? emaFast > emaSlow ? 'bullish' : 'bearish'
    : 'neutral'

  const macdTrend = macd
    ? macd.histogram > 0 ? 'bullish' : 'bearish'
    : 'neutral'

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Indicators</h3>
        <Activity size={14} className="text-blue-400 pulse" />
      </div>

      <div className="space-y-4">
        {/* Price */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#a0a0a0]">Last Price</span>
          <span className="text-lg font-mono font-semibold text-white">
            ${currentBar?.close.toFixed(2) ?? '---'}
          </span>
        </div>

        {/* EMA Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-yellow-400" />
            <span className="text-xs font-semibold text-[#666] uppercase">EMA</span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#1a1a1a] rounded p-2">
              <div className="text-xs text-[#666]">Fast (12)</div>
              <div className="text-sm font-mono font-medium text-yellow-400">
                {emaFast?.toFixed(2) ?? '---'}
              </div>
            </div>
            <div className="bg-[#1a1a1a] rounded p-2">
              <div className="text-xs text-[#666]">Slow (26)</div>
              <div className="text-sm font-mono font-medium text-orange-400">
                {emaSlow?.toFixed(2) ?? '---'}
              </div>
            </div>
          </div>

          <div className={clsx(
            'text-xs font-medium px-2 py-1 rounded text-center',
            emaTrend === 'bullish' && 'bg-green-500/10 text-green-400',
            emaTrend === 'bearish' && 'bg-red-500/10 text-red-400',
            emaTrend === 'neutral' && 'bg-gray-500/10 text-gray-400'
          )}>
            {emaTrend === 'bullish' && 'EMA Bullish Crossover'}
            {emaTrend === 'bearish' && 'EMA Bearish Crossover'}
            {emaTrend === 'neutral' && 'Waiting for signal...'}
          </div>
        </div>

        {/* MACD Section */}
        <div className="space-y-2 pt-3 border-t border-[#2a2a2a]">
          <div className="flex items-center gap-2">
            <BarChart2 size={14} className="text-blue-400" />
            <span className="text-xs font-semibold text-[#666] uppercase">MACD (12, 26, 9)</span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-[#1a1a1a] rounded p-2">
              <div className="text-xs text-[#666]">MACD</div>
              <div className="text-sm font-mono font-medium text-blue-400">
                {macd?.value.toFixed(4) ?? '---'}
              </div>
            </div>
            <div className="bg-[#1a1a1a] rounded p-2">
              <div className="text-xs text-[#666]">Signal</div>
              <div className="text-sm font-mono font-medium text-orange-400">
                {macd?.signal.toFixed(4) ?? '---'}
              </div>
            </div>
            <div className="bg-[#1a1a1a] rounded p-2">
              <div className="text-xs text-[#666]">Histogram</div>
              <div className={clsx(
                'text-sm font-mono font-medium',
                macd && macd.histogram >= 0 ? 'text-green-400' : 'text-red-400'
              )}>
                {macd?.histogram.toFixed(4) ?? '---'}
              </div>
            </div>
          </div>

          <div className={clsx(
            'text-xs font-medium px-2 py-1 rounded text-center',
            macdTrend === 'bullish' && 'bg-green-500/10 text-green-400',
            macdTrend === 'bearish' && 'bg-red-500/10 text-red-400',
            macdTrend === 'neutral' && 'bg-gray-500/10 text-gray-400'
          )}>
            {macdTrend === 'bullish' && 'MACD Above Zero'}
            {macdTrend === 'bearish' && 'MACD Below Zero'}
            {macdTrend === 'neutral' && 'Waiting for signal...'}
          </div>
        </div>

        {/* Combined Signal */}
        <div className="pt-3 border-t border-[#2a2a2a]">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#a0a0a0]">Combined Signal</span>
            {emaTrend !== 'neutral' && macdTrend !== 'neutral' ? (
              <div className={clsx(
                'flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold',
                emaTrend === 'bullish' && macdTrend === 'bullish' && 'bg-green-500/20 text-green-400',
                emaTrend === 'bearish' && macdTrend === 'bearish' && 'bg-red-500/20 text-red-400',
                (emaTrend !== macdTrend) && 'bg-yellow-500/20 text-yellow-400'
              )}>
                {emaTrend === macdTrend ? (
                  <>
                    {emaTrend === 'bullish' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    <span>{emaTrend === 'bullish' ? 'LONG' : 'SHORT'}</span>
                  </>
                ) : (
                  <span>MIXED</span>
                )}
              </div>
            ) : (
              <span className="text-xs text-[#666]">No signal</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
